import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useQuery } from '../../../../constants';
import { updateTour } from '../../../Profile/redux/actions';

import Tours from '../../../Global/Assistant/Tours';
import { DialogContent, DialogTitle, List, ListItem, Button, CircularProgress, Typography } from '@mui/material';
import { ListItemAvatar, ListItemText, Grid, TextField, InputAdornment, Paper } from '@mui/material';
import { ListItemIcon, Avatar, FormControlLabel, Switch, Collapse } from '@mui/material';
import { Table, TableBody, TableHead, TableRow, TableCell, TableContainer } from '@mui/material';
import { SearchRounded, CheckCircleRounded, CheckCircleOutlineRounded } from '@mui/icons-material';
import { SupervisedUserCircleRounded, DeleteOutlineRounded, AddRounded } from '@mui/icons-material';
import { green, grey, red, blue } from '@mui/material/colors';

import { CASES } from '../../../Global/Assistant/Tours/constants';
import { PageContainer, UserGroup, RoundedDialog, StandardDialogActions } from '../../../Global/Components';
import { UserChip, HeaderButton } from '../../../Global/Components';
import PageHeader from '../../../Global/PageHeader';
import StateManager from '../../../Global/StateManager';
import UserManager from '../../../Global/UserManager';
import axios from 'axios';

export default function GroupPage() {
  let query = useQuery();
  const dispatch = useDispatch();
  const isTour = query.get('start_tour');
  const [searchString, setSearchString] = useState('');
  const [groupDialog, setGroupDialog] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const filtered = groups.filter((x) => !searchString || String(x.name).toLowerCase().includes(searchString));

  useEffect(() => {
    axios
      .get('/admin/management/getGroups')
      .then((res) => {
        setGroups(res.data);
        setLoading(false);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setLoading(false);
      });
  }, []);

  function addGroup(group) {
    if (group.deletedId) {
      setGroups(groups.filter((x) => x._id !== group.deletedId));
      return;
    }
    let index = groups.findIndex((x) => x._id === group._id);
    if (index > -1) {
      groups[index] = group;
    } else {
      setGroups([...groups, group]);
    }
  }

  if (loading) {
    return (
      <Grid container alignItems="center" justifyContent="center" style={{ height: '85vh' }}>
        <CircularProgress color="primary" />
      </Grid>
    );
  }

  return (
    <PageContainer justifyContent="flex-start">
      <PageHeader
        icon={<SupervisedUserCircleRounded style={{ color: blue[900], fontSize: '3rem' }} />}
        title="Group Management"
        onSearch={setSearchString}
        dense
        options={
          <HeaderButton
            color="primary"
            text="Add Group"
            className="add-group"
            icon={<AddRounded fontSize="large" />}
            onClick={() => {
              setSelected(null);
              setGroupDialog(true);
            }}
          />
        }
      />
      <Grid item container lg={8} md={8} xs={12} spacing={2}>
        <Grid container item alignItems="center" justifyContent="flex-end">
          {isTour && (
            <Tours
              id={CASES.SETUPGROUPS}
              onTourEnd={() => {
                dispatch(updateTour({ setupGroups: true }));
                setGroupDialog(true);
              }}
              continuous
            />
          )}
        </Grid>
        <Grid container item>
          <TableContainer
            component={Paper}
            variant="outlined"
            style={{ padding: 12, borderRadius: 12, textAlign: 'center' }}
          >
            {groups.length === 0 && (
              <Typography variant="h5" color="textSecondary">
                Nothing here
              </Typography>
            )}
            {filtered.length === 0 && groups.length > 0 && (
              <Typography variant="h5" color="textSecondary">
                No groups match your search
              </Typography>
            )}
            {filtered.length > 0 && (
              <Table aria-label="group-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontSize: 16 }}>Group Name</TableCell>
                    <TableCell style={{ fontSize: 16 }}>Group Users</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((group) => (
                    <TableRow
                      key={group._id}
                      style={{ cursor: 'pointer' }}
                      hover
                      onClick={() => {
                        setSelected(group);
                        setGroupDialog(true);
                      }}
                    >
                      <TableCell style={{ fontSize: 16 }}>{group.name}</TableCell>
                      <TableCell>
                        {group.users && group.users[0] ? (
                          <UserGroup ids={group.users} max={8} dialogTitle={group.name} />
                        ) : (
                          <Typography variant="h5" color="textSecondary">
                            No users
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Grid>
      </Grid>
      <GroupDialog group={selected} open={groupDialog} onClose={() => setGroupDialog(false)} onResult={addGroup} />
    </PageContainer>
  );
}

export function GroupDialog({ open, onClose, onResult, group }) {
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pattern, setPattern] = useState('');
  const [saving, setSaving] = useState(false);
  const [withTimeout, setWithtimeout] = useState(false);
  const [userTimeout, setUserTimeout] = useState(2);
  const [logOutOnClose, setLogOutOnClose] = useState(false);

  useEffect(() => {
    UserManager.getUserList(true)
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    setName(group?.name || '');
    setSelectedUsers(group?.users || []);
    setWithtimeout(!!group?.timeout);
    setUserTimeout(group?.timeout || 2);
    setLogOutOnClose(group?.logOutOnClose || false);
  }, [group]);

  function addDeleteUser(id) {
    let index = selectedUsers.indexOf(id);
    if (index > -1) {
      setSelectedUsers(selectedUsers.filter((x) => x !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  }

  function confirmDelete() {
    StateManager.setConfirm(`You are about to delete group "${group?.name}"`, deleteGroup);
  }

  function deleteGroup() {
    setSaving(true);
    axios
      .post('/admin/management/deleteGroup', { groupId: group?._id })
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('Group has been deleted');
        onResult(res.data);
        onClose();
        setName('');
        setSelectedUsers([]);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  function done() {
    setSaving(true);
    const body = {
      groupId: group?._id,
      name,
      users: selectedUsers,
      timeout: withTimeout ? userTimeout : null,
      logOutOnClose,
    };
    axios
      .post(group ? '/admin/management/saveGroup' : '/admin/management/addGroup', body)
      .then((res) => {
        setSaving(false);
        StateManager.setSuccessAlert('Group has been added');
        onResult(res.data);
        if (logOutOnClose) {
          UserManager.browserCloseLogout();
        } else {
          window.removeEventListener('beforeunload', () => {});
        }
        onClose();
        setName('');
        setSelectedUsers([]);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setSaving(false);
      });
  }

  return (
    <RoundedDialog maxWidth="xs" fullWidth open={open} onClose={() => onClose(null)}>
      <DialogTitle>{group ? 'Edit' : 'New'} group</DialogTitle>
      <DialogContent>
        <Grid container style={{ padding: '8px 0' }}>
          <TextField
            label="Group name"
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
            variant="outlined"
            inputProps={{ maxLength: 64 }}
          />
        </Grid>
        <Grid container style={{ minHeight: 32 }}>
          {selectedUsers.map((id) => (
            <UserChip key={id} id={id} onDelete={() => addDeleteUser(id)} />
          ))}
        </Grid>

        <Grid container item style={{ padding: '8px 0' }}>
          <FormControlLabel
            label="Log users in this group out after period of inactivity"
            onChange={(e) => setWithtimeout(e.target.checked)}
            control={<Switch checked={withTimeout} color="primary" />}
          />
          <Collapse in={withTimeout} style={{ width: '100%' }}>
            <Grid container alignItems="flex-end">
              <Typography style={{ fontWeight: 500 }}>Set timeout:</Typography>
              <TextField
                style={{ width: 150, margin: '5px 8px' }}
                value={userTimeout}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value < 2) return;
                  setUserTimeout(value);
                }}
                label="Timeout"
                type="number"
              />
              <Typography style={{ fontWeight: 500 }}>minutes</Typography>
            </Grid>
          </Collapse>
        </Grid>

        <Grid container item style={{ padding: '8px 0' }}>
          <FormControlLabel
            label="Log users in this group out on browser or tab close"
            onChange={(e) => setLogOutOnClose(e.target.checked)}
            control={<Switch checked={logOutOnClose} color="primary" />}
          />
        </Grid>

        <Grid container style={{ padding: '8px 0' }}>
          <TextField
            placeholder="Search people"
            fullWidth
            value={pattern}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchRounded style={{ color: grey[300] }} />
                </InputAdornment>
              ),
            }}
            onChange={(event) => setPattern(event.target.value.toLowerCase())}
            variant="standard"
          />
        </Grid>
        <Grid container>
          <List style={{ minHeight: '25vh', overflow: 'auto', width: '100%', marginTop: 8 }}>
            {users
              .filter((user) => user.fullName.toLowerCase().includes(pattern))
              .map((user, i) => (
                <ListItem
                  key={user._id}
                  button
                  style={{ borderRadius: 5 }}
                  onClick={() => {
                    addDeleteUser(user._id);
                    setPattern('');
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatar}>{user.avatarLetters}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={user.fullName} />
                  <ListItemIcon style={{ marginLeft: 'auto' }}>
                    {selectedUsers.includes(user._id) ? (
                      <CheckCircleRounded fontSize="large" style={{ color: green[500] }} />
                    ) : (
                      <CheckCircleOutlineRounded fontSize="large" style={{ color: grey[300] }} />
                    )}
                  </ListItemIcon>
                </ListItem>
              ))}
          </List>
        </Grid>
      </DialogContent>
      <StandardDialogActions
        additionalActions={
          group ? (
            <Button
              onClick={confirmDelete}
              style={{ borderRadius: 8, color: red[500], marginRight: 'auto' }}
              startIcon={<DeleteOutlineRounded />}
            >
              delete group
            </Button>
          ) : null
        }
        saving={saving}
        onClose={() => onClose(null)}
        doneButtonText="SAVE"
        onDone={done}
      />
    </RoundedDialog>
  );
}
